import React, { createContext, useState, useEffect, ReactNode, useContext } from 'react';
import { getCategorias } from '../services/categorias';
import { getProveedores, Proveedor } from '../services/proveedores';
import { getUbicaciones } from '../services/ubicaciones';
import { Ubicacion, Departamento } from '../types/index';

interface GlobalData {
  categoriesTree: Departamento[];
  proveedores: Proveedor[];
  ubicaciones: Ubicacion[];
}

interface GlobalContextType {
  data: GlobalData | any; // Almacena la estructura jerárquica
  loading: boolean;
  error: string | null;
  reLoad: () => void;
}

// Crear el contexto
export const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export const useGlobal = (): GlobalContextType => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobal debe ser usado dentro de GlobalProvider');
  }
  return context;
};

interface GlobalProviderProps {
  children: ReactNode;
}

// Proveedor del contexto
export const GlobalProvider: React.FC<GlobalProviderProps> = ({ children }) => {
  const [data, setData] = useState<GlobalData | any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reFetch, setReFetch] = useState<number>(0);

  // Fetch inicial para obtener los departamentos y categorías
  const reLoad = () => {
    setReFetch(prev => prev + 1);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const categoriesResponse = await getCategorias();
        const proveedoresResponse = await getProveedores();
        const ubicacionesResponse = await getUbicaciones();

        const globalData: GlobalData = {
          categoriesTree: categoriesResponse.data,
          proveedores: proveedoresResponse,
          ubicaciones: ubicacionesResponse
        };
        setData(globalData);
      } catch (err) {
        setError('Error al cargar datos');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reFetch]);

  return (
    <GlobalContext.Provider value={{ data, loading, error, reLoad }}>
      {children}
    </GlobalContext.Provider>
  );
};
